"use client";

import { FC } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import { Badge } from "@/components/ui/badge";
import { PrismicNextImage } from "@prismicio/next";
import UndergradSupportCard from "@/app/(landing)/fees-funding/components/undergrad-support-card";
import PostgradSupportCard from "@/app/(landing)/fees-funding/components/postgrad-support-card";
import SFDSupportCard from "@/app/(landing)/fees-funding/components/sfd-support-card";
import { motion } from "framer-motion";

/**
 * Props for `FinancialSupport`.
 */
export type FinancialSupportProps =
  SliceComponentProps<Content.FinancialSupportSlice>;

/**
 * Component for "FinancialSupport" Slices.
 */
const FinancialSupport: FC<FinancialSupportProps> = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      {slice.variation === "default" ? (
        <div className="bg-[#FAFDFF]">
          <Bounded className="py-20">
            {/* tag */}

            <div className="flex justify-center">
              <Badge className="p-4 gap-2 bg-[#D9F0FF] hover:bg-[#D9F0FF] text-primary">
                <PrismicNextImage
                  field={slice.primary.tag_icon}
                  alt=""
                  className="size-6"
                />
                <p className="font-merriweather text-lg">
                  {slice.primary.tag_title}
                </p>
              </Badge>
            </div>

            {/* header */}
            <div className="text-primary space-y-4 flex flex-col md:items-center">
              {/* heading */}

              <motion.div
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                viewport={{ once: true }}
              >
                <PrismicRichText
                  field={slice.primary.heading}
                  components={{
                    heading1: ({ children }) => (
                      <h1 className="text-4xl md:text-5xl font-semibold">
                        {children}
                      </h1>
                    ),
                    heading2: ({ children }) => (
                      <h2 className="text-4xl md:text-5xl font-semibold">
                        {children}
                      </h2>
                    ),
                    heading3: ({ children }) => (
                      <h3 className="text-4xl md:text-5xl font-semibold">
                        {children}
                      </h3>
                    ),
                    heading4: ({ children }) => (
                      <h4 className="text-4xl md:text-5xl font-semibold">
                        {children}
                      </h4>
                    ),
                    heading5: ({ children }) => (
                      <h5 className="text-4xl md:text-5xl font-semibold">
                        {children}
                      </h5>
                    ),
                    heading6: ({ children }) => (
                      <h6 className="text-4xl md:text-5xl font-semibold">
                        {children}
                      </h6>
                    ),
                  }}
                />
              </motion.div>

              {/* subheading */}

              <motion.div
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.4, ease: "easeInOut" }}
                viewport={{ once: true }}
              >
                <PrismicRichText
                  field={slice.primary.sub_heading}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="font-merriweather leading-8">{children}</p>
                    ),
                  }}
                />
              </motion.div>
            </div>

            {/* support cards */}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* undergrad */}
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.8, ease: "easeInOut" }}
                viewport={{ once: true }}
              >
                <UndergradSupportCard data={slice.primary} />
              </motion.div>
              {/* postgrad */}

              <motion.div
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, delay: 1, ease: "easeInOut" }}
                viewport={{ once: true }}
              >
                <PostgradSupportCard data={slice.primary} />
              </motion.div>
              {/* SFD */}

              <motion.div
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, delay: 1.2, ease: "easeInOut" }}
                viewport={{ once: true }}
                className="lg:col-span-2"
              >
                <SFDSupportCard
                  data={slice.primary}
                />
              </motion.div>
            </div>
          </Bounded>
        </div>
      ) : null}
    </section>
  );
};

export default FinancialSupport;
